// Shared types for Desert Storm plan variants.
// Structurally parallel to `types.ts` for CS but with the DS building set.

import type { Priority, VariantMeta } from './types';

export type DSSlotKey =
  | 'infoCenter'
  | 'arsenal'
  | 'mercenary'
  | 'nuclearSilo'
  | 'scienceHub'
  | 'fieldHospital1'
  | 'fieldHospital2'
  | 'fieldHospital3'
  | 'fieldHospital4'
  | 'oilRefinery1'
  | 'oilRefinery2';

export type DSNonSiloSlot = Exclude<DSSlotKey, 'nuclearSilo'>;

// Iteration order for the home-based fill (matches the "flex-analogue is
// nuclearSilo" convention). Priority-ordered so higher-priority slots take
// stronger displaced players first when needed.
export const DS_NON_SILO_SLOTS: DSNonSiloSlot[] = [
  'infoCenter',
  'oilRefinery1',
  'oilRefinery2',
  'fieldHospital1',
  'fieldHospital2',
  'scienceHub',
  'arsenal',
  'mercenary',
  'fieldHospital3',
  'fieldHospital4',
];

export type DSPhaseDef = {
  title: string;
  time: string;
  timeColor: string;
  subtitle: string;
  subtitleColor: string;
  priorities: Record<DSSlotKey, Priority | null>;
  /** Absolute player count per slot. Sum across all slots = roster size. */
  capacities: Record<DSSlotKey, number>;
  /**
   * Top-N-by-M1 claims applied before home-based fill (§ collapse phase).
   * Same semantics as `PhaseDef.strengthClaims` in CS.
   */
  strengthClaims?: { slot: DSSlotKey; count: number }[];
};

export type DSPlanConfig = {
  meta: VariantMeta;
  phases: DSPhaseDef[];
  /** Home-slot allocation by strength rank (rank 0 = strongest non-flex). */
  homeByRank: DSNonSiloSlot[];
  /** Ranks that never receive a sub pairing (analog of CS PT core). */
  coreRanks: readonly number[];
  /**
   * Optional note text shown inside a slot tile. Rendered centered when the
   * tile is closed/abandoned; rendered as a small caption above the input
   * rows when the tile is active.
   */
  slotNotes?: Partial<Record<DSSlotKey, string>>;
  /**
   * Slots that stage flex players and should render with the blue "flex"
   * styling. Silo is included automatically when applicable. Used by the
   * single-map variant to also mark Arsenal + Mercenary as flex zones.
   */
  flexSlots?: DSSlotKey[];
};
